<template>
  <nav class="fixed w-full top-0 z-50 backdrop-blur-xl bg-white/70 dark:bg-gray-900/70 border-b border-gray-100/50 dark:border-gray-800/50 transition-colors duration-300">
    <div class="max-w-5xl mx-auto px-4 py-3 flex justify-between items-center">
      <!-- Brand -->
      <Link href="/" class="group flex items-center gap-2.5">
        <span class="w-8 h-8 rounded-lg bg-amber-500 group-hover:bg-amber-600 transition-colors flex items-center justify-center shrink-0">
          <span class="text-white font-extrabold font-mono text-xs tracking-tight">pvm</span>
        </span>
        <span class="text-sm font-semibold text-gray-500 dark:text-gray-400 font-mono group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors hidden sm:block">pvpmartins</span>
      </Link>

      <!-- Desktop Navigation -->
      <div class="hidden md:flex items-center gap-4">
        <ul class="flex space-x-6">
          <li>
            <Link href="/articles" class="relative text-gray-600 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors font-medium">
              {{ t('articles') }}
            </Link>
          </li>
          <li>
            <button @click="scrollToSection('#about')" class="relative text-gray-600 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors font-medium">
              {{ t('about') }}
            </button>
          </li>
          <li>
            <button @click="scrollToSection('#contact')" class="relative text-gray-600 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors font-medium">
              {{ t('contact') }}
            </button>
          </li>
        </ul>

        <div class="flex items-center gap-2">
          <!-- Language toggle -->
          <div class="flex items-center bg-gray-100/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg p-0.5">
            <button
              v-for="loc in availableLocales"
              :key="loc.code"
              @click="locale = loc.code"
              class="px-1.5 py-0.5 text-xs rounded-md transition-all"
              :class="locale === loc.code
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'"
            >
              {{ loc.flag }}
            </button>
          </div>

          <!-- Dark mode toggle -->
          <button
            @click="toggleDarkMode"
            class="p-2.5 rounded-xl bg-gray-100/80 dark:bg-gray-800/80 backdrop-blur-sm hover:bg-gray-200/80 dark:hover:bg-gray-700/80 transition-all border border-transparent hover:border-amber-200 dark:hover:border-amber-800"
            :aria-label="isDark ? t('switchToLight') : t('switchToDark')"
          >
            <svg v-if="isDark" class="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/>
            </svg>
            <svg v-else class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile menu button -->
      <button
        @click="mobileMenuOpen = !mobileMenuOpen"
        class="md:hidden p-2 rounded-xl bg-gray-100/80 dark:bg-gray-800/80 backdrop-blur-sm"
        aria-label="Toggle menu"
      >
        <svg v-if="!mobileMenuOpen" class="w-6 h-6 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
        </svg>
        <svg v-else class="w-6 h-6 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>

    <!-- Mobile menu -->
    <div
      v-show="mobileMenuOpen"
      class="md:hidden border-t border-gray-100/50 dark:border-gray-800/50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl"
    >
      <div class="px-4 py-4 space-y-4">
        <ul class="space-y-3">
          <li>
            <Link href="/articles" @click="mobileMenuOpen = false" class="block py-2 text-gray-600 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors font-medium">
              {{ t('articles') }}
            </Link>
          </li>
          <li>
            <button @click="scrollToSection('#about')" class="block py-2 text-gray-600 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors font-medium">
              {{ t('about') }}
            </button>
          </li>
          <li>
            <button @click="scrollToSection('#contact')" class="block py-2 text-gray-600 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors font-medium">
              {{ t('contact') }}
            </button>
          </li>
        </ul>

        <div class="flex items-center gap-3 pt-2 border-t border-gray-100/50 dark:border-gray-800/50">
          <!-- Language toggle -->
          <div class="flex items-center bg-gray-100/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl p-1">
            <button
              v-for="loc in availableLocales"
              :key="loc.code"
              @click="locale = loc.code"
              class="px-3 py-2 text-sm rounded-lg transition-all"
              :class="locale === loc.code
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'"
            >
              {{ loc.flag }}
            </button>
          </div>

          <!-- Dark mode toggle -->
          <button
            @click="toggleDarkMode"
            class="p-2.5 rounded-xl bg-gray-100/80 dark:bg-gray-800/80 backdrop-blur-sm hover:bg-gray-200/80 dark:hover:bg-gray-700/80 transition-all border border-transparent hover:border-amber-200 dark:hover:border-amber-800"
            :aria-label="isDark ? t('switchToLight') : t('switchToDark')"
          >
            <svg v-if="isDark" class="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/>
            </svg>
            <svg v-else class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { Link, usePage, router } from '@inertiajs/vue3'
import { useI18n } from '@/i18n'

const { t, locale, availableLocales } = useI18n()
const page = usePage()

const isDark = ref(false)
const mobileMenuOpen = ref(false)
let mediaQuery = null

const updateDarkMode = (dark) => {
  isDark.value = dark
  document.documentElement.classList.toggle('dark', dark)
}

const toggleDarkMode = () => {
  const newDark = !isDark.value
  updateDarkMode(newDark)
  localStorage.setItem('darkMode', newDark ? 'true' : 'false')
}

const handleSystemChange = (e) => {
  const saved = localStorage.getItem('darkMode')
  if (saved === null) {
    updateDarkMode(e.matches)
  }
}

// Handle hash navigation - scroll if on home page, navigate otherwise
const scrollToSection = (hash) => {
  mobileMenuOpen.value = false
  
  // Check if we're on the home page (component is 'Home')
  const isHomePage = page.component === 'Home'
  
  if (isHomePage) {
    // Already on home page, just scroll
    const element = document.querySelector(hash)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
      history.pushState(null, '', hash)
    }
  } else {
    // Navigate to home page with hash - the navigate event will handle scrolling
    router.visit(`/${hash}`)
  }
}

onMounted(() => {
  mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  const saved = localStorage.getItem('darkMode')

  if (saved !== null) {
    updateDarkMode(saved === 'true')
  } else {
    updateDarkMode(mediaQuery.matches)
  }

  mediaQuery.addEventListener('change', handleSystemChange)
})

onUnmounted(() => {
  if (mediaQuery) {
    mediaQuery.removeEventListener('change', handleSystemChange)
  }
})
</script>
